import * as React from 'react';
import {
  makeStyles, TextField, FormControl, InputLabel, MenuItem, Select,
  StandardTextFieldProps, SelectProps, FormHelperText, Grid, OutlinedInput
} from '@material-ui/core';
import { OverviewList, OverviewMobile } from './components/OverviewList.component';
import { FormikProps } from 'formik';
import {
  TimeOption, Settings, Contact, AppointmentType
} from './np-types';
import { PhoneField } from './PhoneInput.component';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '1000px',
    width: 'fit-content',
    paddingLeft: '24px',
    paddingRight: '24px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  overviewContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '10px',
    marginBottom: '10px'
  },
  dateTimeContainer: {
    marginLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    width: '550px'
  },
  hideMobile: {
  },
  showMobile: {
    display: 'none'
  },
  infoColumn: {
    marginRight: '20px',
    display: 'flex',
    flexDirection: 'column'
  },
  typeOfScheduling: {
    fontSize: 20,
  },
  '@media screen and (max-width: 765px)': {
    overviewContainer: {
      flexDirection: 'column'
    },
    dateTimeContainer: {
      marginLeft: 0,
      width: '100%',
    },
    showMobile: {
      display: 'block'
    },
    hideMobile: {
      display: 'none'
    },
    typeOfScheduling: {
      display: 'none',
    },
  },
  '@media screen and (max-width: 992px)': {
    dateTimeContainerPicker: {
      flexDirection: 'column',
    }
  },
  formControl: {
    marginBottom: '20px',
    width: '100%'
  },
  link: {
    cursor: 'pointer',
    color: '#3795DD',
    whiteSpace: 'nowrap',
  },
  container: {
    marginTop: 6,

    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',

      '& span': {
        marginLeft: 10,
      }
    }
  },
  selectRoot: {
    '&:focus': {
      backgroundColor: 'transparent'
    }
  }
}));


interface MyTextFieldProps extends StandardTextFieldProps {
  form: FormikProps<Contact>;
  name: keyof Contact;
}

const MyTextField = ({ form, name, ...props }: MyTextFieldProps) => {
  return <TextField
    fullWidth
    id={props.id || name}
    name={name}
    label={props.label}
    error={form.touched[name] && Boolean(form.errors[name])}
    helperText={form.touched[name] && form.errors[name]}
    onBlur={form.handleBlur}
    onChange={form.handleChange}
    value={form.values[name]}
    InputProps={{ style: { borderRadius: 10 } }}
    variant='outlined'
    size='small'
    {...props}
    style={{ width: '100%' }} />;
};

interface MyPhoneFieldProps extends StandardTextFieldProps {
  form: FormikProps<Contact>;
  name: keyof Contact;
  label: string;
}

const MyPhoneField = ({ form, name, ...props }: MyPhoneFieldProps) => {
  return <PhoneField
    id={props.id || name}
    name={name}
    label={props.label}
    error={form.touched[name] && Boolean(form.errors[name])}
    helperText={form.touched[name] && form.errors[name]}
    onBlur={form.handleBlur}
    onChange={form.handleChange}
    value={form.values[name]}
    onCountryCodeChange={(c) => form.setFieldValue('properPhoneLength', c, false)}
    newStyle
  />;
};

interface MySelectProps extends SelectProps {
  form: FormikProps<Contact>;
  name: keyof Contact;
  label: string;
}

const MySelect: React.FC<MySelectProps> = ({
  form, name, children, ...props
}) => {
  const classes = useStyles();
  return <FormControl variant='outlined' size='small' style={{ width: '100%' }}>
    <InputLabel id={name + '-label'} shrink={true}>Referrer</InputLabel>
    <Select
      labelId={name + '-label'}
      id={name}
      displayEmpty
      name={name}
      onBlur={form.handleBlur}
      onChange={form.handleChange}
      value={form.values[name]}
      variant='outlined'
      style={{ borderRadius: 10 }}
      classes={{ root: classes.selectRoot }}
      input={
        <OutlinedInput
          notched
          label={'Referrer'}
        />
      }
      {...props} >
      {children}
    </Select>
    <FormHelperText>{form.touched[name] && form.errors[name]}</FormHelperText>
  </FormControl>;
};


interface ContactPageProps {
  date: Date;
  time: TimeOption
  settings: Settings;
  form: FormikProps<Contact>;
  selectedApptType: AppointmentType;
  back: () => void;
  apiKey?: string;
  gotoOther: (s: string) => void;
  autoFocus: number;
  setModalText: (o: {
    title: string, body: string, actions?: React.ReactNode[]
  }) => void;
}

export const ContactPage = ({
  date, time, form, settings, back, apiKey, gotoOther, autoFocus,
  setModalText
}: ContactPageProps) => {
  const classes = useStyles();
  const referrers = settings.nppType === 'New' &&
    (settings.referrals.filter(r => !!r) || ['Google', 'Facebook', 'Other']);

  const [prompt, linkedUrl, linkPrompt] = React.useMemo(() => {
    if (!settings.linkedKey) {
      return ['', '', ''];
    }
    if (settings.nppType === 'New') {
      return [
        'Have you been with us before?',
        `?key=${settings.linkedKey}&from=${apiKey}`,
        'Returning Patients Here'
      ];
    } else {
      return [
        'Is this your first time with us?',
        `?key=${settings.linkedKey}&from=${apiKey}`,
        'New Patients Here'
      ];
    }
  }, [settings]);

  return (
    <div className={classes.overviewContainer}>
      <div className={classes.infoColumn + ' ' + classes.hideMobile}>
        {settings.logoUrl &&
          <img
            src={settings.logoUrl}
            alt={settings.name}
            style={{ width: '100%', maxWidth: '300px', height: 'auto' }}
          />}
        <OverviewList
          date={date}
          time={time}
          timezone={settings.timezone}
        />
        <div
          style={{ maxWidth: '400px' }}
          dangerouslySetInnerHTML={{
            __html: settings.landingPageDescription
          }}>
        </div>
      </div>
      <div className={classes.showMobile}>
        <OverviewMobile
          nav={back}
          date={date}
          time={time}
          timezone={settings.timezone}
          nppType={settings.nppType}
        />
      </div>

      <div className={classes.dateTimeContainer}>
        <div className={classes.typeOfScheduling}>
          <b>{settings.nppType} Patient</b> <i>Scheduling</i>
        </div>
        <h4>{settings.contactDirection}</h4>
        <Grid container spacing={2} style={{ marginBottom: 10 }}>
          <Grid item xs={12} md={6}>
            <MyTextField
              name="firstName"
              label="First Name"
              form={form}
              autoFocus={autoFocus === 0}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MyTextField
              name="lastName"
              label="Last Name"
              form={form}
              autoFocus={autoFocus === 1}
            />
          </Grid>
          {settings.nppType !== 'Existing' && (
            <Grid item xs={12} md={6}>
              <MyPhoneField
                name="phone"
                label="Phone number"
                form={form}
                autoFocus={autoFocus === 2}
              />
            </Grid>
          )}
          {settings.nppType !== 'Existing' && (
            <Grid item xs={12} md={6}>
              <MyTextField
                name="email"
                label="Email"
                placeholder="name@example.com"
                type="email"
                form={form}
                autoFocus={autoFocus === 3}
              />
            </Grid>
          )}
          {(settings.birthdayOption !== 'Hidden' &&
            settings.nppType === 'New') &&
            <Grid item xs={12} md={6}>
              <MyTextField
                name="birthday"
                label="Birthday"
                placeholder="MM-DD-YYYY"
                InputLabelProps={{ shrink: true }}
                type="date"
                form={form}
                autoFocus={autoFocus === 4}
              />
            </Grid>
          }
          {referrers && referrers.length > 0 &&
            <Grid item xs={12} md={6}>
              <MySelect
                label="Referrer"
                name="referrer"
                form={form}
              >
                <MenuItem value="" disabled><em>Please pick one</em></MenuItem>
                {referrers.map(r => <MenuItem value={r} key={r}>{r}</MenuItem>)}
              </MySelect>
            </Grid>
          }
          {form.values.referrer === 'Other' &&
            <Grid item xs={12} md={6}>
              <MyTextField
                name="otherReferrer"
                label="Other Referrer"
                form={form} />
            </Grid>
          }
        </Grid>

        {settings.nppType === 'Existing' && (
          <Grid>
            <div>Enter phone number or email</div>
            <Grid container spacing={1} className={classes.container}>
              <Grid item style={{ flex: 1 }}>
                <MyPhoneField
                  name="phone"
                  label="Phone number"
                  form={form}
                  autoFocus={autoFocus === 2}
                />
              </Grid>
              <Grid style={{ display: 'flex', alignItems: 'center' }}>
                <span>or</span>
              </Grid>
              <Grid item style={{ flex: 1 }}>
                <MyTextField
                  name="email"
                  label="Email"
                  placeholder="name@example.com"
                  type="email"
                  form={form}
                  autoFocus={autoFocus === 3}
                />
              </Grid>
            </Grid>
          </Grid>
        )}

        {settings.nppType === 'New' &&
          <i style={{ marginBottom: '10px' }}>
            By submitting this form you are consenting to receive text messages from {settings.name}
          </i>}
        
        {prompt &&
          <p style={{ color: '#444F53' }}>
            <span style={{ marginRight: 3 }}>
              {prompt}
            </span>
            <span
              className={classes.link}
              onClick={() => {
                if (settings.linkedKey)
                  gotoOther(linkedUrl);
                else
                  setModalText({
                    title: 'Contact Our Office',
                    body: `We apologize for the inconvenience. This scheduler isn\'t built for ${linkPrompt.split(' ')[0].toLowerCase()} patients. We kindly ask you to contact our office.`,
                  });
              }}
            >
              {linkPrompt}
            </span>
          </p>}
      </div>
    </div>
  );
};
